import { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc, orderBy, query, updateDoc, writeBatch } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

const EventsManager = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchEvents = async () => {
        try {
            const q = query(collection(db, "events"), orderBy("order", "asc"));
            const querySnapshot = await getDocs(q);
            const eventsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setEvents(eventsData);
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const handleMove = async (id, direction) => {
        const currentIndex = events.findIndex(e => e.id === id);
        if (currentIndex === -1) return;

        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        // Boundary checks
        if (targetIndex < 0 || targetIndex >= events.length) return;

        const currentEvent = events[currentIndex];
        const targetEvent = events[targetIndex];

        // Optimistic UI update
        const newEvents = [...events];
        newEvents[currentIndex] = { ...targetEvent, order: currentEvent.order };
        newEvents[targetIndex] = { ...currentEvent, order: targetEvent.order };
        // Sort by order to keep list consistent visually
        newEvents.sort((a, b) => a.order - b.order);
        setEvents(newEvents);

        try {
            const batch = writeBatch(db);
            const currentRef = doc(db, "events", currentEvent.id);
            const targetRef = doc(db, "events", targetEvent.id);

            // Swap orders in DB
            batch.update(currentRef, { order: targetEvent.order });
            batch.update(targetRef, { order: currentEvent.order });

            await batch.commit();
        } catch (error) {
            console.error("Error moving event:", error);
            alert("Sıralama değiştirilemedi.");
            fetchEvents(); // Revert on error
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Bu etkinliği silmek istediğinize emin misiniz?")) {
            try {
                await deleteDoc(doc(db, "events", id));
                setEvents(events.filter(event => event.id !== id));
            } catch (error) {
                console.error("Error deleting event:", error);
                alert("Silme işlemi başarısız oldu.");
            }
        }
    };

    if (loading) return <div className="text-white">Yükleniyor...</div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-white">Etkinlikler</h1>
                <Link
                    to="/admin/events/new"
                    className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                    <Plus size={20} />
                    <span>Yeni Ekle</span>
                </Link>
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                {/* Desktop Table */}
                <table className="w-full text-left text-gray-400 hidden md:table">
                    <thead className="bg-gray-700/50 text-gray-200 uppercase text-xs">
                        <tr>
                            <th className="px-6 py-4 w-16 text-center">Sıra</th>
                            <th className="px-6 py-4">Başlık</th>
                            <th className="px-6 py-4">Tarih</th>
                            <th className="px-6 py-4 text-right">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                        {events.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                    Henüz hiç etkinlik eklenmemiş.
                                </td>
                            </tr>
                        ) : (
                            events.map((event, index) => (
                                <tr key={event.id} className="hover:bg-gray-700/30 transition-colors">
                                    <td className="px-6 py-4 flex flex-col items-center gap-1">
                                        <button
                                            onClick={() => handleMove(event.id, 'up')}
                                            disabled={index === 0}
                                            className="text-gray-500 hover:text-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ArrowUp size={16} />
                                        </button>
                                        <span className="text-xs font-mono text-gray-500">{event.order}</span>
                                        <button
                                            onClick={() => handleMove(event.id, 'down')}
                                            disabled={index === events.length - 1}
                                            className="text-gray-500 hover:text-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <ArrowDown size={16} />
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-white">{event.title}</td>
                                    <td className="px-6 py-4">{event.date}</td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-3">
                                            <Link
                                                to={`/admin/events/edit/${event.id}`}
                                                className="text-blue-400 hover:text-blue-300 p-1 rounded-md hover:bg-blue-400/10 transition-colors"
                                            >
                                                <Edit2 size={18} />
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(event.id)}
                                                className="text-red-400 hover:text-red-300 p-1 rounded-md hover:bg-red-400/10 transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Mobile Card View */}
                <div className="md:hidden flex flex-col divide-y divide-gray-700">
                    {events.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            Henüz hiç etkinlik eklenmemiş.
                        </div>
                    ) : (
                        events.map((event, index) => (
                            <div key={event.id} className="p-4 flex gap-4 items-center">
                                {/* Order Controls (Left) */}
                                <div className="flex flex-col items-center gap-2 bg-gray-700/30 p-2 rounded-lg">
                                    <button
                                        onClick={() => handleMove(event.id, 'up')}
                                        disabled={index === 0}
                                        className="p-2 bg-gray-700 rounded text-gray-300 hover:bg-cyan-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ArrowUp size={20} />
                                    </button>
                                    <span className="font-mono font-bold text-cyan-400">{event.order}</span>
                                    <button
                                        onClick={() => handleMove(event.id, 'down')}
                                        disabled={index === events.length - 1}
                                        className="p-2 bg-gray-700 rounded text-gray-300 hover:bg-cyan-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ArrowDown size={20} />
                                    </button>
                                </div>

                                {/* Content (Middle) */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-white font-bold truncate text-lg mb-1">{event.title}</h3>
                                    <div className="text-gray-400 text-sm flex items-center gap-2">
                                        <span>{event.date}</span>
                                    </div>
                                </div>

                                {/* Actions (Right) */}
                                <div className="flex flex-col gap-2">
                                    <Link
                                        to={`/admin/events/edit/${event.id}`}
                                        className="p-3 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500 hover:text-white transition-colors"
                                    >
                                        <Edit2 size={20} />
                                    </Link>
                                    <button
                                        onClick={() => handleDelete(event.id)}
                                        className="p-3 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500 hover:text-white transition-colors"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventsManager;
